import React from 'react';
import { Helmet } from 'react-helmet-async';
import PageLayout from '@/components/PageLayout';

const TermsOfService: React.FC = () => {
  return (
    <PageLayout showFooter={false}>
      <Helmet>
        <title>Terms of Service - CIPHER Security</title>
        <meta name="description" content="Terms of Service and conditions for using CIPHER Security's website and cybersecurity services." />
      </Helmet>
      
      <div className="container mx-auto px-4 py-32">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-white mb-8">Terms of Service</h1>
          <div className="text-gray-300 space-y-6 leading-relaxed">
            <p className="text-sm text-gray-400">
              <strong>Last Updated:</strong> December 30, 2024
            </p>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Acceptance of Terms</h2>
              <p>
                By accessing and using the CIPHER Security website ("Service") and engaging with our cybersecurity 
                services, you accept and agree to be bound by the terms and provision of this agreement. 
                If you do not agree to abide by the above, please do not use this service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Description of Service</h2>
              <p className="mb-4">
                CIPHER Security provides professional cybersecurity services including but not limited to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Offensive Security Services:</strong> Penetration testing, vulnerability assessments, red team exercises</li>
                <li><strong>Application Security:</strong> Secure code review, SAST/DAST testing, security architecture review</li>
                <li><strong>Cloud Security:</strong> Cloud infrastructure security, compliance assessments, configuration reviews</li>
                <li><strong>DevSecOps:</strong> Security automation, CI/CD security integration, security tooling</li>
                <li><strong>Security Advisory Services:</strong> Risk assessments, compliance consulting, security strategy</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. User Obligations and Responsibilities</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">3.1 Lawful Use</h3>
                  <p>
                    You agree to use our website and services only for lawful purposes and in a way that does not 
                    infringe the rights of, restrict, or inhibit anyone else's use and enjoyment of the website.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">3.2 Accurate Information</h3>
                  <p>
                    You agree to provide accurate, current, and complete information when requested and to 
                    update such information to keep it accurate, current, and complete.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">3.3 Prohibited Activities</h3>
                  <p className="mb-2">You agree not to:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Use our services for any illegal or unauthorized purpose</li>
                    <li>Attempt to gain unauthorized access to any portion of the website or systems</li>
                    <li>Interfere with or disrupt the service or servers connected to the service</li>
                    <li>Transmit any viruses, malware, or other malicious code</li>
                    <li>Spam our contact forms or abuse our communication channels</li>
                    <li>Reverse engineer, decompile, or disassemble any portion of the service</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Service Availability and Modifications</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">4.1 Service Availability</h3>
                  <p>
                    We strive to maintain high availability of our website and services, but we do not guarantee 
                    uninterrupted access. The service may be temporarily unavailable for maintenance, updates, 
                    or due to circumstances beyond our control.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">4.2 Modifications</h3>
                  <p>
                    We reserve the right to modify, suspend, or discontinue any aspect of the service at any time 
                    without prior notice. We may also impose limits on certain features or restrict access to 
                    parts or all of the service.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Professional Services Terms</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">5.1 Service Agreements</h3>
                  <p>
                    Professional cybersecurity services are governed by separate service agreements, statements of work, 
                    or contracts that supplement these Terms of Service. In case of conflict, the specific service 
                    agreement takes precedence.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">5.2 Scope Limitations</h3>
                  <p>
                    All security testing and assessment services are performed within explicitly defined scope and 
                    authorized environments only. Any testing outside the agreed scope requires written authorization.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">5.3 Confidentiality</h3>
                  <p>
                    All information shared during the course of professional services is treated as confidential and 
                    subject to non-disclosure agreements. We implement strict data handling and security procedures 
                    to protect client information.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Intellectual Property Rights</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">6.1 Website Content</h3>
                  <p>
                    The content, organization, graphics, design, and other matters related to the website are 
                    protected under applicable copyrights and other proprietary laws. The copying, redistribution, 
                    or publication of any part of this site is prohibited.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">6.2 Service Deliverables</h3>
                  <p>
                    Reports, assessments, and other deliverables created during professional services engagement 
                    are the property of the client, while the underlying methodologies, tools, and knowledge 
                    remain the property of CIPHER Security.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Privacy and Data Protection</h2>
              <p>
                Your privacy is important to us. Our collection and use of personal information is governed by 
                our Privacy Policy, which is incorporated into these Terms of Service by reference. 
                Please review our Privacy Policy to understand our practices.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Disclaimers and Limitations of Liability</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">8.1 Service Disclaimer</h3>
                  <p>
                    The website and services are provided "as is" without any representations or warranties, 
                    express or implied. We make no representations or warranties in relation to this website 
                    or the information and materials provided on this website.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">8.2 Professional Services Disclaimer</h3>
                  <p>
                    While we use industry-standard methodologies and best practices, cybersecurity assessments 
                    cannot guarantee the identification of all vulnerabilities or threats. Security is an ongoing 
                    process that requires continuous attention and improvement.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">8.3 Limitation of Liability</h3>
                  <p>
                    To the maximum extent permitted by law, CIPHER Security shall not be liable for any indirect, 
                    incidental, special, consequential, or punitive damages, or any loss of profits or revenue, 
                    whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Indemnification</h2>
              <p>
                You agree to defend, indemnify, and hold harmless CIPHER Security and its affiliates, officers, 
                directors, employees, and agents from and against any claims, damages, obligations, losses, 
                liabilities, costs, or debt arising from your use of the service or violation of these Terms of Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Rate Limiting and Anti-Abuse</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">10.1 Contact Form Limits</h3>
                  <p>
                    To prevent spam and abuse, our contact form is limited to 3 submissions per hour per IP address. 
                    Excessive or automated submissions may result in temporary or permanent blocking.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">10.2 Monitoring and Logging</h3>
                  <p>
                    We monitor website usage and maintain logs for security and operational purposes. 
                    Suspicious activity may be investigated and reported to appropriate authorities.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Termination</h2>
              <p>
                We may terminate or suspend your access to the service immediately, without prior notice or liability, 
                for any reason whatsoever, including without limitation if you breach the Terms of Service. 
                Upon termination, your right to use the service will cease immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Governing Law and Jurisdiction</h2>
              <p>
                These Terms of Service shall be interpreted and governed by the laws of Canada. 
                Any disputes arising from these terms or the use of our services shall be subject to 
                the exclusive jurisdiction of the Canadian courts.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">13. Severability</h2>
              <p>
                If any provision of these Terms of Service is found to be unenforceable or invalid, 
                that provision will be limited or eliminated to the minimum extent necessary so that 
                the Terms of Service will otherwise remain in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">14. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms of Service at any time. We will notify users 
                of any material changes by posting the updated terms on our website. Your continued use 
                of the service after such modifications constitutes acceptance of the updated terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">15. Contact Information</h2>
              <p>
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="mt-4 p-4 bg-gray-800 rounded-lg">
                <p><strong>CIPHER Security</strong></p>
                <p>Email: <a href="mailto:sales@ciphersecurity.io" className="text-blue-400 hover:text-blue-300">sales@ciphersecurity.io</a></p>
                <p>Website: <a href="https://ciphersecurity.io" className="text-blue-400 hover:text-blue-300">https://ciphersecurity.io</a></p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">16. Entire Agreement</h2>
              <p>
                These Terms of Service, together with our Privacy Policy and Cookie Policy, 
                constitute the entire agreement between you and CIPHER Security regarding the use of our website. 
                This agreement supersedes all prior or contemporaneous communications and proposals.
              </p>
            </section>

            <div className="mt-12 p-6 bg-gray-800 rounded-lg border border-gray-700">
              <p className="text-sm text-gray-400">
                <strong>Acknowledgment:</strong> By using this website and our services, you acknowledge that you 
                have read these Terms of Service, understand them, and agree to be bound by them.
              </p>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default TermsOfService;